import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';
import { handleAuthError } from './authUtils';

// BackButton component (same as in other components)
const BackButton = ({ language }) => {
  const navigate = useNavigate();
  
  return (
    <button
      onClick={() => navigate('/mecz/turniej-rankingowy')}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none'
      }}
      onMouseOver={(e) => e.target.style.color = '#333'}
      onMouseOut={(e) => e.target.style.color = '#888'}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = ({ language }) => {
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = getTranslation('menu', language);
        } else if (seg === 'turniej-rankingowy') {
          displayText = getTranslation('tournament', language);
        } else if (seg === 'statystyki-meczow') {
          displayText = getTranslation('statystyki-meczow-turniejowych', language);
        } else if (seg === 'mecz') {
          // Skip the intermediate 'mecz' segment
          return null;
        } else {
          displayText = seg;
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

// Linear Chart Component
const LinearChart = ({ games, timeRange, language }) => {
  // Mobile detection
  const isMobile = window.innerWidth <= 600;
  
  // State for mobile tooltip
  const [mobileTooltip, setMobileTooltip] = React.useState(null);
  
  // Sort games by date and take only the last 100 games
  const sortedGames = [...games]
    .sort((a, b) => new Date(a.gamedate) - new Date(b.gamedate))
    .slice(-100); // Take only the last 100 games
  
  // Calculate cumulative averages
  const calculateCumulativeAverages = (games) => {
    const data = [];
    let totalPointsWon = 0;
    let totalPointsLost = 0;
    
    games.forEach((game, index) => {
      // Handle null/undefined values explicitly
      const pointsWon = game.playerscore !== null && game.playerscore !== undefined ? game.playerscore : 0;
      const pointsLost = game.opponentscore !== null && game.opponentscore !== undefined ? game.opponentscore : 0;
      
      totalPointsWon += pointsWon;
      totalPointsLost += pointsLost;
      
      data.push({
        gameNumber: index + 1,
        date: new Date(game.gamedate),
        avgPointsWon: totalPointsWon / (index + 1),
        avgPointsLost: totalPointsLost / (index + 1),
        pointsWon: pointsWon,
        pointsLost: pointsLost
      });
    });
    
    return data;
  };
  
  const chartData = calculateCumulativeAverages(sortedGames);
  
  // Handle mobile touch events
  const handleMobileTouch = (point, type, event) => {
    if (!isMobile) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const tooltipText = type === 'won' 
      ? `${getTranslation('mecz', language)} ${point.gameNumber}: ${point.pointsWon} ${getTranslation('pkt-zdobyte', language)} | ${getTranslation('srednia', language)}: ${point.avgPointsWon.toFixed(2)}`
      : `${getTranslation('mecz', language)} ${point.gameNumber}: ${point.pointsLost} ${getTranslation('pkt-stracone', language)} | ${getTranslation('srednia', language)}: ${point.avgPointsLost.toFixed(2)}`;
    
    setMobileTooltip({
      text: tooltipText,
      x: event.clientX || event.touches?.[0]?.clientX || 0,
      y: event.clientY || event.touches?.[0]?.clientY || 0,
      type: type
    });
  };
  
  // Close mobile tooltip when clicking elsewhere
  const handleChartClick = (event) => {
    if (isMobile && mobileTooltip) {
      setMobileTooltip(null);
    }
  };
  
  if (chartData.length === 0) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        textAlign: 'center'
      }}>
        <p style={{ color: '#666', fontFamily: 'Nunito, Arial, sans-serif' }}>
          {getTranslation('brak-danych-do-wyswietlenia', language)}
        </p>
      </div>
    );
  }
  
  // Use fixed scale from 0 to 13
  const minValue = 0;
  const maxValue = 13;
  const range = maxValue - minValue;
  
  const chartHeight = 300;
  const chartWidth = isMobile ? Math.max(400, window.innerWidth - 40) : 800;
  const margin = { top: 20, right: 20, bottom: 40, left: 60 };
  
  const scaleY = (value) => {
    return chartHeight - margin.bottom - ((value - minValue) / range) * (chartHeight - margin.top - margin.bottom);
  };
  
  const scaleX = (index) => {
    if (chartData.length === 1) return chartWidth / 2; // Center if only one point
    return margin.left + (index / (chartData.length - 1)) * (chartWidth - margin.left - margin.right);
  };
  
  // Generate path for line
  const generatePath = (data, valueKey) => {
    if (data.length === 0) return '';
    if (data.length === 1) {
      const x = scaleX(0);
      const y = scaleY(data[0][valueKey]);
      return `M ${x} ${y} L ${x} ${y}`;
    }
    
    return data.map((point, index) => {
      const x = scaleX(index);
      const y = scaleY(point[valueKey]);
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    }).join(' ');
  };
  
  const wonPath = generatePath(chartData, 'avgPointsWon');
  const lostPath = generatePath(chartData, 'avgPointsLost');
  
  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      textAlign: 'center'
    }}>
      <h3 style={{
        color: '#1e3a8a',
        fontFamily: 'Nunito, Arial, sans-serif',
        marginBottom: '20px',
        fontSize: '1.3rem'
      }}>
        {getTranslation('srednie-punkty-w-czasie', language)}
      </h3>
      
      <div style={{ 
        display: 'flex', 
        justifyContent: isMobile ? 'flex-start' : 'center', 
        marginBottom: '20px',
        overflowX: isMobile ? 'auto' : 'visible',
        padding: isMobile ? '0 10px' : '0'
      }}>
        <svg 
          width={chartWidth} 
          height={chartHeight} 
          style={{ 
            border: '1px solid #e5e7eb', 
            borderRadius: '8px',
            minWidth: isMobile ? '400px' : 'auto'
          }}
          onClick={handleChartClick}
        >
          {/* Grid lines */}
          {[0, 2, 4, 6, 8, 10, 12].map(value => (
            <line
              key={value}
              x1={margin.left}
              y1={scaleY(value)}
              x2={chartWidth - margin.right}
              y2={scaleY(value)}
              stroke="#e5e7eb"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
          ))}
          
          {/* Y-axis labels */}
          {[0, 2, 4, 6, 8, 10, 12].map(value => (
            <text
              key={value}
              x={margin.left - 10}
              y={scaleY(value) + 5}
              textAnchor="end"
              fontSize="12"
              fill="#6b7280"
              fontFamily="Nunito, Arial, sans-serif"
            >
              {value}
            </text>
          ))}
          
          {/* Legend */}
          <g>
            <line
              x1={chartWidth - 150}
              y1={20}
              x2={chartWidth - 120}
              y2={20}
              stroke="#10b981"
              strokeWidth="3"
            />
            <text
              x={chartWidth - 110}
              y={25}
              fontSize="12"
              fill="#374151"
              fontFamily="Nunito, Arial, sans-serif"
              fontWeight="600"
            >
              {getTranslation('punkty-zdobyte', language)}
            </text>
            
            <line
              x1={chartWidth - 150}
              y1={40}
              x2={chartWidth - 120}
              y2={40}
              stroke="#ef4444"
              strokeWidth="3"
            />
            <text
              x={chartWidth - 110}
              y={45}
              fontSize="12"
              fill="#374151"
              fontFamily="Nunito, Arial, sans-serif"
              fontWeight="600"
            >
              {getTranslation('punkty-stracone', language)}
            </text>
          </g>
          
          {/* Lines */}
          <path
            d={wonPath}
            fill="none"
            stroke="#10b981"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={lostPath}
            fill="none"
            stroke="#ef4444"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Data points */}
          {chartData.map((point, index) => (
            <g key={index}>
              <circle
                cx={scaleX(index)}
                cy={scaleY(point.avgPointsWon)}
                r="4"
                fill="#10b981"
                style={{ cursor: 'pointer' }}
                onTouchStart={(e) => handleMobileTouch(point, 'won', e)}
                onClick={(e) => handleMobileTouch(point, 'won', e)}
              >
                <title>
                  {getTranslation('mecz', language)} {point.gameNumber}: {point.pointsWon} {getTranslation('pkt-zdobyte', language)} | {getTranslation('srednia', language)}: {point.avgPointsWon.toFixed(2)}
                </title>
              </circle>
              <circle
                cx={scaleX(index)}
                cy={scaleY(point.avgPointsLost)}
                r="4"
                fill="#ef4444"
                style={{ cursor: 'pointer' }}
                onTouchStart={(e) => handleMobileTouch(point, 'lost', e)}
                onClick={(e) => handleMobileTouch(point, 'lost', e)}
              >
                <title>
                  {getTranslation('mecz', language)} {point.gameNumber}: {point.pointsLost} {getTranslation('pkt-stracone', language)} | {getTranslation('srednia', language)}: {point.avgPointsLost.toFixed(2)}
                </title>
              </circle>
            </g>
          ))}
          
          {/* Axes */}
          <line
            x1={margin.left}
            y1={margin.top}
            x2={margin.left}
            y2={chartHeight - margin.bottom}
            stroke="#374151"
            strokeWidth="2"
          />
          <line
            x1={margin.left}
            y1={chartHeight - margin.bottom}
            x2={chartWidth - margin.right}
            y2={chartHeight - margin.bottom}
            stroke="#374151"
            strokeWidth="2"
          />
          
          {/* X-axis label */}
          <text
            x={chartWidth / 2}
            y={chartHeight - 10}
            textAnchor="middle"
            fontSize="14"
            fill="#374151"
            fontFamily="Nunito, Arial, sans-serif"
            fontWeight="600"
          >
            {getTranslation('numer-meczu', language)}
          </text>
          
          {/* Y-axis label */}
          <text
            x={15}
            y={chartHeight / 2}
            textAnchor="middle"
            fontSize="14"
            fill="#374151"
            fontFamily="Nunito, Arial, sans-serif"
            fontWeight="600"
            transform={`rotate(-90, 15, ${chartHeight / 2})`}
          >
            {getTranslation('srednie-punkty', language)}
          </text>
        </svg>
        
        {/* Mobile Tooltip */}
        {isMobile && mobileTooltip && (
          <div
            style={{
              position: 'fixed',
              left: `${mobileTooltip.x}px`,
              top: `${mobileTooltip.y - 60}px`,
              backgroundColor: mobileTooltip.type === 'won' ? '#10b981' : '#ef4444',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontWeight: '600',
              zIndex: 1000,
              maxWidth: '250px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              transform: 'translateX(-50%)',
              pointerEvents: 'none'
            }}
          >
            {mobileTooltip.text}
          </div>
        )}
      </div>
      
      <div style={{
        marginTop: '20px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        fontFamily: 'Nunito, Arial, sans-serif',
        fontSize: '14px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div>
            <strong>{getTranslation('lacznie-meczow', language)}:</strong> {chartData.length}
          </div>
        </div>
      </div>
    </div>
  );
};

// Pie Chart Component
const PieChart = ({ data, title, totalGames, wonGames, lostGames, language }) => {
  const wonPercentage = totalGames > 0 ? Math.round((wonGames / totalGames) * 100) : 0;
  const lostPercentage = totalGames > 0 ? Math.round((lostGames / totalGames) * 100) : 0;
  
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const wonStrokeDasharray = totalGames > 0 ? (wonGames / totalGames) * circumference : 0;
  const lostStrokeDasharray = totalGames > 0 ? (lostGames / totalGames) * circumference : 0;
  
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      minWidth: '200px'
    }}>
      <h3 style={{ 
        margin: '0 0 15px 0', 
        color: '#1e3a8a', 
        fontFamily: 'Nunito, Arial, sans-serif',
        fontSize: '1.2rem',
        textAlign: 'center'
      }}>
        {title}
      </h3>
      
      <div style={{ position: 'relative', marginBottom: '20px' }}>
        <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="20"
          />
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            stroke="#10b981"
            strokeWidth="20"
            strokeDasharray={`${wonStrokeDasharray} ${circumference}`}
            strokeLinecap="round"
          />
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            stroke="#ef4444"
            strokeWidth="20"
            strokeDasharray={`${lostStrokeDasharray} ${circumference}`}
            strokeDashoffset={-wonStrokeDasharray}
            strokeLinecap="round"
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif',
          fontWeight: 'bold',
          fontSize: '1.2rem',
          color: '#1e3a8a'
        }}>
          {totalGames}
        </div>
      </div>
      
      <div style={{ textAlign: 'center', fontFamily: 'Nunito, Arial, sans-serif' }}>
        <div style={{ marginBottom: '8px' }}>
          <span style={{ color: '#10b981', fontWeight: 'bold' }}>●</span>
          <span style={{ marginLeft: '8px' }}>{getTranslation('wygrane', language)}: {wonGames} ({wonPercentage}%)</span>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <span style={{ color: '#ef4444', fontWeight: 'bold' }}>●</span>
          <span style={{ marginLeft: '8px' }}>{getTranslation('przegrane', language)}: {lostGames} ({lostPercentage}%)</span>
        </div>
        <div style={{ 
          marginTop: '10px', 
          padding: '8px', 
          background: '#f8f9fa', 
          borderRadius: '6px',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          {getTranslation('lacznie', language)}: {totalGames} {getTranslation('meczow', language)}
        </div>
      </div>
    </div>
  );
};

const TournamentMatchStats = () => {
  const { language } = useLanguage();
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthError, setIsAuthError] = useState(false);
  const [timeRange, setTimeRange] = useState('all'); // 'all', 'year', 'month'
  const [roundType, setRoundType] = useState('all'); // 'all', 'B', 'P', 'G'

  // Fetch games data
  useEffect(() => {
    const fetchGames = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        console.log('Fetching tournament games for stats, token present:', !!token);
        
        if (!token) {
          throw new Error(getTranslation('authError', language));
        }
        
        console.log('Making request to /api/games');
        const response = await fetch('/api/games', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          if (response.status === 401) {
            setIsAuthError(true);
            setError(getTranslation('authError', language));
            setLoading(false);
            handleAuthError(response);
            return;
          }
          const errorText = await response.text();
          console.log('Error response:', errorText);
          setIsAuthError(false);
          throw new Error(`Błąd ${response.status}: ${errorText}`);
        }
        
        const data = await response.json();
        console.log('All games data:', data);
        
        // Filter only tournament games with ranking roundtypes (G, P, B)
        const tournamentGames = data.filter(game => 
          game.tournament !== null && 
          ['G', 'P', 'B'].includes(game.roundtype)
        );
        
        console.log('Tournament games (G,P,B):', tournamentGames);
        setGames(tournamentGames);
        setFilteredGames(tournamentGames);
        setLoading(false);
      } catch (err) {
        console.error('Fetch error:', err);
        setIsAuthError(false);
        setError(`${getTranslation('connectionError', language)}: ${err.message}`);
        setLoading(false);
      }
    };

    fetchGames();
  }, []);

  // Filter games based on time range and roundtype
  useEffect(() => {
    let filtered = games;
    const now = new Date();
    
    // Filter by time range
    if (timeRange === 'year') {
      const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      filtered = games.filter(game => new Date(game.gamedate) >= oneYearAgo);
    } else if (timeRange === 'month') {
      const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      filtered = games.filter(game => new Date(game.gamedate) >= oneMonthAgo);
    }
    
    // Filter by roundtype
    if (roundType !== 'all') {
      filtered = filtered.filter(game => game.roundtype === roundType);
    }
    
    setFilteredGames(filtered);
  }, [games, timeRange, roundType]);

  // Calculate statistics for different game types
  const calculateStats = (games) => {
    const totalGames = games.length;
    const wonGames = games.filter(game => game.playerscore > game.opponentscore).length;
    const lostGames = games.filter(game => game.playerscore < game.opponentscore).length;
    
    return { totalGames, wonGames, lostGames };
  };

  const allStats = calculateStats(filteredGames);
  const singleStats = calculateStats(filteredGames.filter(game => game.numberofplayers === 1));
  const doubleStats = calculateStats(filteredGames.filter(game => game.numberofplayers === 2));
  const tripleStats = calculateStats(filteredGames.filter(game => game.numberofplayers === 3));

  if (loading) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          {getTranslation('shootMe', language)}
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{getTranslation('ladowanie-danych', language)}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          {getTranslation('shootMe', language)}
        </h1>
        
        {/* Back button and breadcrumbs in horizontal layout */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: '20px',
          margin: '24px 0'
        }}>
          <BackButton language={language} />
          <Breadcrumbs language={language} />
        </div>
        
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h2 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.5rem'
            }}>
              {getTranslation('blad-ladowania-statystyk', language)}
            </h2>
            <p style={{ color: 'red', fontFamily: 'Nunito, Arial, sans-serif', marginBottom: '20px' }}>
              {error}
            </p>
            <button 
              onClick={() => isAuthError ? window.location.href = '/' : window.location.reload()} 
              style={{
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontSize: '14px'
              }}
            >
              {isAuthError ? getTranslation('przejdzDoStronyGlownej', language) : getTranslation('sprobuj-ponownie', language)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton language={language} />
        <Breadcrumbs language={language} />
      </div>
      
      {/* Time Range Filter */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
        margin: '0 auto 20px auto'
      }}>
        <h3 style={{ 
          margin: '0 0 15px 0', 
          color: '#1e3a8a', 
          fontFamily: 'Nunito, Arial, sans-serif' 
        }}>
          {getTranslation('zakres-czasowy', language)}
        </h3>
        <div style={{ 
          display: 'flex', 
          gap: '15px',
          flexWrap: 'wrap'
        }}>
          {[
            { value: 'all', label: getTranslation('wszystkie', language) },
            { value: 'year', label: getTranslation('rok', language) },
            { value: 'month', label: getTranslation('miesiac', language) }
          ].map(option => (
            <label key={option.value} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="timeRange"
                value={option.value}
                checked={timeRange === option.value}
                onChange={(e) => setTimeRange(e.target.value)}
                style={{ margin: 0 }}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>
      
      {/* Round Type Filter */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
        margin: '0 auto 20px auto'
      }}>
        <h3 style={{ 
          margin: '0 0 15px 0', 
          color: '#1e3a8a', 
          fontFamily: 'Nunito, Arial, sans-serif' 
        }}>
          {getTranslation('typ', language)}
        </h3>
        <div style={{ 
          display: 'flex', 
          gap: '15px',
          flexWrap: 'wrap'
        }}>
          {[
            { value: 'all', label: getTranslation('wszystkie', language) },
            { value: 'B', label: getTranslation('buchholz', language) },
            { value: 'P', label: getTranslation('pucharowe', language) },
            { value: 'G', label: getTranslation('grupowe', language) }
          ].map(option => (
            <label key={option.value} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              <input
                type="radio"
                name="roundType"
                value={option.value}
                checked={roundType === option.value}
                onChange={(e) => setRoundType(e.target.value)}
                style={{ margin: 0 }}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>
      
      {/* Pie Charts */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
        margin: '0 auto 20px auto'
      }}>
        <h2 style={{
          color: '#1e3a8a',
          fontFamily: 'Nunito, Arial, sans-serif',
          marginBottom: '20px',
          fontSize: '1.5rem',
          textAlign: 'center'
        }}>
          {getTranslation('statystyki-meczow-turniejowych', language)}
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(4, 1fr)',
          gap: '20px',
          justifyContent: 'center'
        }}>
          <PieChart
            title={getTranslation('wszystkie-mecze', language)}
            totalGames={allStats.totalGames}
            wonGames={allStats.wonGames}
            lostGames={allStats.lostGames}
            language={language}
          />
          <PieChart
            title={getTranslation('single', language)}
            totalGames={singleStats.totalGames}
            wonGames={singleStats.wonGames}
            lostGames={singleStats.lostGames}
            language={language}
          />
          <PieChart
            title={getTranslation('dublety', language)}
            totalGames={doubleStats.totalGames}
            wonGames={doubleStats.wonGames}
            lostGames={doubleStats.lostGames}
            language={language}
          />
          <PieChart
            title={getTranslation('triplety', language)}
            totalGames={tripleStats.totalGames}
            wonGames={tripleStats.wonGames}
            lostGames={tripleStats.lostGames}
            language={language}
          />
        </div>
      </div>

      {/* Linear Chart */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {filteredGames.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#666', fontFamily: 'Nunito, Arial, sans-serif' }}>
              {getTranslation('dodaj-mecze-aby-zobaczyc-statystyki', language)}
            </p>
          </div>
        ) : (
          <LinearChart games={filteredGames} timeRange={timeRange} language={language} />
        )}
      </div>
    </div>
  );
};

export default TournamentMatchStats;